import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TeamSection from '../components/TeamSection';
import StatsSection from '../components/StatsSection';
import { MapPin } from 'lucide-react';

const AboutUsPage = () => {
  const [pickup, setPickup] = useState('');
  const [drop, setDrop] = useState('');

  const handleBookRide = () => {
    const userAgent = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent);

    if (isIOS || isMac) {
      window.open('https://apps.apple.com/az/app/nura-mobility/id6749004862', '_blank');
    } else {
      window.open('https://play.google.com/store/apps/details?id=in.co.nuraemobility.nuraApplication', '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section with Booking and Phone Image */}
      <section className="pt-32 pb-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Booking Form with right margin */}
            <div className="space-y-6 lg:pl-12">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-8">
                Book Your Electric Ride
              </h1>

              {/* Pickup Location */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                  <MapPin size={24} className="text-gray-600" />
                </div>
                <input
                  type="text"
                  value={pickup}
                  onChange={(e) => setPickup(e.target.value)}
                  onClick={handleBookRide}
                  readOnly
                  placeholder="Enter Pickup Location"
                  className="w-full pl-14 pr-4 py-6 bg-white rounded-2xl border-2 border-gray-200 focus:border-[#bceb39] focus:outline-none text-lg text-gray-700 placeholder-gray-400 cursor-pointer"
                />
              </div>

              {/* Drop Location */}
              <div className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                  <MapPin size={24} className="text-gray-600" />
                </div>
                <input
                  type="text"
                  value={drop}
                  onChange={(e) => setDrop(e.target.value)}
                  onClick={handleBookRide}
                  readOnly
                  placeholder="Enter Drop Location"
                  className="w-full pl-14 pr-4 py-6 bg-white rounded-2xl border-2 border-gray-200 focus:border-[#bceb39] focus:outline-none text-lg text-gray-700 placeholder-gray-400 cursor-pointer"
                />
              </div>

              {/* Book Ride Button */}
              <button
                onClick={handleBookRide}
                className="w-full bg-[#bceb39] text-black font-bold py-6 px-8 rounded-2xl hover:bg-[#a8d632] transition-colors duration-300 text-xl"
              >
                Book Ride
              </button>

              {/* Download App Text */}
              <p className="text-center text-gray-400 text-sm">
                Download the Nura app to book your eco-friendly ride
              </p>
            </div>

            {/* Right Side - Phone Image */}
            <div className="flex justify-center lg:justify-end">
              <img
                src="https://customer-assets.emergentagent.com/job_nurae-electric/artifacts/8rptozq5_Untitled%20design%20%281%29.png"
                alt="Nura App"
                className="w-full max-w-md h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* A New Era Begins with Our Promise Combined */}
      <section className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">A New Era Begins</h2>
                <p className="text-xl text-gray-300 leading-relaxed">
                  NURA is reimagining urban mobility through a company-owned fleet of electric vehicles. 
                  We deliver zero-emission transportation focused on cleaner cities, inclusive opportunities, 
                  and reliable everyday mobility that puts people and the planet first.
                </p>
              </div>

              <div className="pt-8">
                <h3 className="text-3xl font-bold text-white mb-6">Our Promise</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#bceb39] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-lg text-gray-300">Guaranteed rides with <span className="font-bold text-[#bceb39]">zero cancellations</span></p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#bceb39] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-lg text-gray-300">Quick <span className="font-bold text-[#bceb39]">5-minute pickups</span> for everyday city travel</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-[#bceb39] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-lg text-gray-300"><span className="font-bold text-[#bceb39]">100% electric mobility</span>, built for a cleaner planet</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Zero Emissions Image Box - Vertically Centered, Square */}
            <div className="flex items-center justify-center h-full">
              <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-3xl shadow-xl flex flex-col items-center justify-center p-4" style={{ width: '288px', height: '288px' }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_green-ride-2/artifacts/8f0ozc2a_N%20transparent.png" 
                  alt="NURA Logo" 
                  className="w-32 h-32 object-contain mb-2"
                />
                <p className="text-[#bceb39] text-2xl font-bold leading-tight">Zero Emissions</p>
                <p className="text-white text-lg leading-tight">Powered By NURA</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Destination and The Journey - Side by Side */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - The Destination */}
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">The Destination</h2>
              <p className="text-xl font-bold text-white">
                To power India's transition to clean urban mobility.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Creating sustainable, accessible, and inclusive electric transportation.
              </p>
            </div>

            {/* Right - The Journey */}
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">The Journey</h2>
              <p className="text-xl font-bold text-white">
                To build a new era of urban mobility through a company-owned fleet of electric vehicles.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Delivering reliable, people-first transportation designed for everyday life.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Team Section */}
      <TeamSection />
      
      <Footer />
    </div>
  );
};

export default AboutUsPage;
